// What needs the person on the phone (Poulpe-624): the questions put to them
// and the landings that wait for them, in Fabien's order (Poulpe-622 record
// 21): urgent questions, then approvals, then the rest, the questions in the
// order the dock uses (questions/waiting.tsx, `byNeed`).
//
// - The landings are those the desktop counts (sessions/attention.ts,
//   `landingsAwaitingPerson`): those asking for an approval, and the batches
//   that stopped and hold their proposals, until dismissed. The phone approves
//   the first with a passkey (ApprovalScreen.tsx) and shows the second read
//   only.
// - A question whose answer is on its way from this phone (answers.tsx) is
//   listed as kept or sending, not as needing an answer, and is not counted.
import type { ApprovalAsked, Issue, Landing, Project, Proposal, Question, Session } from '../api/types'
import { stoppedText } from '../board/merge'
import { byNeed } from '../questions/waiting'
import type { BoardState } from '../store/store'
import { needsYou } from '../store/store'
import type { Kept } from './answers'

export type NeedItem =
  | { kind: 'question'; id: string; question: Question; issue: Issue | null; project: Project | null; session: Session | null; going: 'kept' | 'sending' | null }
  | { kind: 'approval'; id: string; landing: Landing; asked: ApprovalAsked; project: Project | null; issues: Issue[] }
  | { kind: 'held'; id: string; landing: Landing; why: string; project: Project | null; issues: Issue[] }

/** The issues some proposals carry, each once, in order. */
export function carriedIssues(proposalIds: readonly string[], proposals: ReadonlyMap<string, Proposal>, issues: ReadonlyMap<string, Issue>): Issue[] {
  const ids = proposalIds.flatMap((id) => {
    const proposal = proposals.get(id)
    return proposal ? [proposal.issue_id, ...proposal.issue_ids] : []
  })
  return [...new Set(ids)].flatMap((id) => issues.get(id) ?? [])
}

/** The issues the proposals of an approval's subject carry. */
export function approvalIssues(asked: ApprovalAsked, proposals: ReadonlyMap<string, Proposal>, issues: ReadonlyMap<string, Issue>): Issue[] {
  return carriedIssues(asked.subject.proposals, proposals, issues)
}

/** A landing's approval, while it asks for one. */
export function approvalOf(landing: Landing): ApprovalAsked | null {
  if ((landing.state !== 'running' && landing.state !== 'deferred') || landing.waiting_for?.for !== 'approval') return null
  const { for: _for, ...asked } = landing.waiting_for
  return asked
}

/** Why a batch that stopped holds its proposals, in one line, its proposals named by their issues' keys. */
export function heldWhy(landing: Landing, proposals: ReadonlyMap<string, Proposal>, issues: ReadonlyMap<string, Issue>): string {
  const keyOf = (id: string) => carriedIssues([id], proposals, issues)[0]?.key ?? 'a proposal'
  return stoppedText(landing.outcome, keyOf) ?? 'It stopped'
}

type NeedState = Pick<BoardState, 'questions' | 'pendingAnswers' | 'me' | 'issues' | 'projects' | 'sessions' | 'landings' | 'held' | 'dismissed' | 'proposals'>

/** Everything that waits for the signed-in person at `now`, in the phone's order; `kept`, the answers on their way from this phone. */
export function needItems(state: NeedState, now: number, kept: ReadonlyMap<string, Kept> = new Map()): NeedItem[] {
  const { issues, projects, sessions, proposals } = state
  const asked = needsYou(state.questions, state.me?.member?.id, state.pendingAnswers).filter((q) => q.state === 'open')
  const questions = byNeed(asked, now).map((question): NeedItem & { kind: 'question' } => {
    const issue = question.issue ? (issues.get(question.issue) ?? null) : null
    const phase = kept.get(question.id)?.phase
    const going = phase === 'kept' || phase === 'sending' ? phase : null
    return { kind: 'question', id: question.id, question, issue, project: issue ? (projects.get(issue.project) ?? null) : null, session: sessions.get(question.session) ?? null, going }
  })
  const approvals = [...state.landings.values()].flatMap((landing): NeedItem[] => {
    const approval = approvalOf(landing)
    if (!approval) return []
    return [{ kind: 'approval', id: landing.id, landing, asked: approval, project: projects.get(landing.project_id) ?? null, issues: approvalIssues(approval, proposals, issues) }]
  })
  const held = [...state.held.values()].filter((landing) => !state.dismissed.has(landing.id)).map((landing): NeedItem => (
    { kind: 'held', id: landing.id, landing, why: heldWhy(landing, proposals, issues), project: projects.get(landing.project_id) ?? null, issues: carriedIssues(landing.proposals, proposals, issues) }
  ))
  return [...questions.filter((i) => i.question.urgent), ...approvals, ...held, ...questions.filter((i) => !i.question.urgent)]
}

/** How many of them wait for the person: not those whose answer is on its way. */
export function waitingCount(items: readonly NeedItem[]): number {
  return items.filter((item) => item.kind !== 'question' || item.going === null).length
}
